import React from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import styled from "@emotion/styled";

const Intro = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2)
}));

const FigureList = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(2)
}));

const FigureRow = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(0.75, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius
}));

const FigureLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm
}));

const FigureValue = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
    fontSize: theme.typography.size.sm,
    whiteSpace: "nowrap"
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(0.5)
}));

const SectionHint = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm,
    marginBottom: theme.spacing(1)
}));

const MoveRow = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1)
}));

const MoveHeading = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightSemiBold
}));

const MoveDetail = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary
}));

const formatHours = (value) => `${Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })} h`;

const formatCurrency = (value) =>
    Number(value || 0).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatCount = (value, singular, plural) => `${Number(value || 0).toLocaleString("en-US")} ${Number(value) === 1 ? singular : plural}`;

const describeEntity = (entity) => {
    if (!entity) return "no billing entity";
    if (entity.name) return `${entity.name} (${entity.type})`;
    return entity.type;
};

const BillingImpactDialog = ({ open, impact, summary, onClose, closeLabel = "Got it" }) => {
    if (!impact && !summary) return null;

    const unbilled = impact?.unbilled || {};
    const duePayments = unbilled.duePayments || { count: 0, amount: 0 };
    const rebilling = impact?.rebilling || [];
    const hidesWork = !!impact?.hidesWork;
    const level = impact?.level ? impact.level.toLowerCase() : "record";

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>What this deletion did to unbilled work</DialogTitle>
            <DialogContent>
                <Intro variant="body2">
                    The {level} was deleted. Nothing was blocked, but money that had not been invoiced yet was affected, so here is where it went.
                </Intro>

                {summary && <Alert severity="warning">{summary}</Alert>}

                <FigureList>
                    {unbilled.timeRecords > 0 && (
                        <FigureRow>
                            <FigureLabel>Unbilled time</FigureLabel>
                            <FigureValue>
                                {formatHours(unbilled.hours)} across {formatCount(unbilled.timeRecords, "record", "records")}
                            </FigureValue>
                        </FigureRow>
                    )}
                    {unbilled.charges > 0 && (
                        <FigureRow>
                            <FigureLabel>Pending charges</FigureLabel>
                            <FigureValue>
                                {formatCurrency(unbilled.chargeAmount)} across {formatCount(unbilled.charges, "charge", "charges")}
                            </FigureValue>
                        </FigureRow>
                    )}
                    {duePayments.count > 0 && (
                        <FigureRow>
                            <FigureLabel>Due payments</FigureLabel>
                            <FigureValue>
                                {formatCurrency(duePayments.amount)} across {formatCount(duePayments.count, "payment", "payments")}
                            </FigureValue>
                        </FigureRow>
                    )}
                </FigureList>

                {hidesWork ? (
                    <>
                        <SectionTitle variant="subtitle2">This work is now hidden</SectionTitle>
                        <SectionHint>
                            Deleting cascaded down, so this work is off every invoice and off the unbilled report until the {level} is restored. If any of it
                            still needs to be billed, restore the {level} first and rerun billing for the period.
                        </SectionHint>
                    </>
                ) : (
                    <>
                        <SectionTitle variant="subtitle2">This work moved</SectionTitle>
                        <SectionHint>
                            The work itself was not deleted. It now bills against the next billing entity above it, which may change the rate it bills at and
                            which invoice it lands on.
                        </SectionHint>
                    </>
                )}

                {rebilling.length > 0 && (
                    <>
                        <SectionTitle variant="subtitle2">Where it goes now</SectionTitle>
                        {rebilling.map((move, index) => (
                            <MoveRow key={`${describeEntity(move.from)}-${describeEntity(move.to)}-${index}`}>
                                <MoveHeading>
                                    {describeEntity(move.from)} to {describeEntity(move.to)}
                                </MoveHeading>
                                <MoveDetail>
                                    {[
                                        move.timeRecords > 0 ? `${formatHours(move.hours)} across ${formatCount(move.timeRecords, "time record", "time records")}` : null,
                                        move.charges > 0 ? `${formatCurrency(move.chargeAmount)} across ${formatCount(move.charges, "charge", "charges")}` : null
                                    ]
                                        .filter(Boolean)
                                        .join(", ")}
                                </MoveDetail>
                                {move.orphanReason && <MoveDetail>It will not reach an invoice there: {move.orphanReason}</MoveDetail>}
                            </MoveRow>
                        ))}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={onClose}>
                    {closeLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BillingImpactDialog;
